import * as React from "react";
import { Clock, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/components/utils/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface TimePickerProps {
  value?: string;
  onChange?: (time: string) => void;
  placeholder?: string;
  hasError?: boolean;
  className?: string;
  disabled?: boolean;
}

const TimePicker = React.forwardRef<HTMLButtonElement, TimePickerProps>(
  ({ value, onChange, placeholder = "Select time", hasError = false, className, disabled, ...props }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);
    
    // Convert time string to Date object for display
    const timeValue = value ? new Date(`2000-01-01T${value}`) : null;
    
    // Generate time options (every 15 minutes)
    const timeOptions = React.useMemo(() => {
      const options = [];
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          const timeDate = new Date(`2000-01-01T${timeString}`);
          options.push({
            value: timeString,
            label: format(timeDate, 'HH:mm')
          });
        }
      }
      return options;
    }, []);

    const handleTimeSelect = (timeString: string) => {
      onChange?.(timeString);
      setIsOpen(false);
    };

    const displayValue = timeValue ? format(timeValue, 'HH:mm') : placeholder;

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            className={cn(
              "w-full justify-start text-left font-normal h-10 hover:bg-gray-100 border-input rounded-md",
              !value && "text-muted-foreground",
              hasError && "border-yellow-400",
              className
            )}
            disabled={disabled}
            {...props}
          >
            <Clock className="mr-2 h-4 w-4 text-gray-900" />
            {displayValue}
            <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-0" align="start">
          <div className="max-h-60 overflow-y-auto">
            {timeOptions.map((option) => (
              <Button
                key={option.value}
                variant="ghost"
                className="w-full rounded-none justify-start text-left font-normal h-8 px-3"
                onClick={() => handleTimeSelect(option.value)}
              >
                <span className="font-mono text-sm">{option.label}</span>
              </Button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

TimePicker.displayName = "TimePicker";

export { TimePicker };
